// ---------- the charter, as the canvas needs it ----------
//
// The stylesheet is where the charter lives (`:root` in css/style.css) and it is meant to stay
// the only place: switching charters should be that one block, not a hunt through the drawing
// code. The canvas cannot use var(), so it reads the tokens ONCE here and composes its own
// colours from them.
//
// Once, and not per call: a fine grid sets fillStyle thousands of times per frame, and
// getComputedStyle forces a style recalculation every time — the same reason inkBase() memoises
// its own read. Lazily, and not at module load: the tokens only exist after the stylesheet is
// in, and an import is not a guarantee of that.
//
// The tokens come as TRIPLETS (`--acc-rgb: 201, 242, 75`) with the plain colour derived from
// them (`--acc: rgb(var(--acc-rgb))`), which is what lets the same value serve `var(--acc)` in
// a CSS rule and `rgba(…, .3)` here. The alpha stays on this side: how transparent a wash is
// belongs to the drawing, not to the charter.

import { markHue } from './mark';

/** Falls back to the values of the DEFAULT theme (« Clair »), so a canvas drawn before the
 *  stylesheet lands is merely a frame early rather than a frame of `rgba(undefined)` — which
 *  paints nothing at all. Un joueur en « Ciel » ou en « Soir » verrait donc, dans ce cas très
 *  court, l'accent du thème clair : c'est un compromis assumé, et le seul autre choix serait
 *  de dupliquer les trois thèmes ici, où ils se désaccorderaient de la charte au premier
 *  changement. */
const FALLBACK: Record<string, string> = {
  '--paper-rgb': '251, 246, 236',
  '--acc-rgb': '123, 92, 240',
  '--acc-light-rgb': '214, 203, 255',
  '--fresh-rgb': '255, 92, 168',
  '--red-rgb': '179, 24, 48',
  '--rule-rgb': '60, 52, 92',
  '--shade-rgb': '44, 35, 80',
  '--on-guide-rgb': '21, 15, 46',
  '--mark-citron-rgb': '201, 242, 75',
  '--mark-citron-light-rgb': '244, 255, 210',
  '--mark-citron-deep-rgb': '78, 107, 0',
  '--mark-citron-soft-rgb': '228, 255, 138',
  '--mark-ambre-rgb': '255, 162, 61',
  '--mark-ambre-light-rgb': '255, 231, 199',
  '--mark-ambre-deep-rgb': '138, 62, 0',
  '--mark-ambre-soft-rgb': '255, 199, 138',
  '--mark-rose-rgb': '255, 92, 168',
  '--mark-rose-light-rgb': '255, 214, 233',
  '--mark-rose-deep-rgb': '135, 0, 66',
  '--mark-rose-soft-rgb': '255, 138, 192',
};

function triplet(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || FALLBACK[name];
}

function build() {
  // DEUX SOMBRES, et non le fond du thème. Les deux couleurs composées plus bas à partir de
  // celles-ci — l'ombre dans laquelle la feuille tombe pendant la récolte, et le chevron de la
  // boussole — se composaient à partir de --bg-rgb, ce qui a tenu tant que le fond était mûre :
  // elles étaient sombres parce que le fond l'était. Avec un thème clair, la même formule donne
  // une « ombre » lavande qui n'assombrit rien et un chevron pâle sur un disque vif, c'est-à-dire
  // deux effets qui disparaissent. Ce sont donc des jetons à part, tenus hors des thèmes.
  const shade = triplet('--shade-rgb');
  const onGuide = triplet('--on-guide-rgb');
  // La teinte du repère, choisie par le joueur. Lue ici et pas ailleurs : tout ce qui suit s'en
  // compose, et changer de teinte se résume alors à jeter ce cache (cf. resetPal).
  const tok = markHue().token;
  const mk = triplet(`${tok}-rgb`);
  const mkLight = triplet(`${tok}-light-rgb`);
  const mkDeep = triplet(`${tok}-deep-rgb`);
  // Le ton clair-mais-vif, celui que l'accent appelle acc2. Ajouté pour la charte CSS de
  // l'interface, il sert ici à la récolte — et pour le citron il vaut acc2 AU CHIFFRE PRÈS,
  // ce qui est la propriété qui compte : choisir « citron » redonne l'écran d'avant.
  const mkSoft = triplet(`${tok}-soft-rgb`);
  const paper = triplet('--paper-rgb');
  const acc = triplet('--acc-rgb');
  const light = triplet('--acc-light-rgb');
  const fresh = triplet('--fresh-rgb');
  const red = triplet('--red-rgb');
  const rule = triplet('--rule-rgb');
  const a = (rgb: string, alpha: number) => `rgba(${rgb}, ${alpha})`;
  return {
    /** The sheet itself. */
    paper: `rgb(${paper})`,
    /** The plate a zone count sits on: the paper's own colour, barely see-through. */
    plate: a(paper, 0.88),
    /** The zone lattice — thin over the numbers, stronger once they are gone and it is all
     *  that ties the cells under the finger back to the map.
     *
     *  À LA TEINTE DU REPÈRE, comme tout ce qui suit jusqu'à la boussole.
     *
     *  LA FRONTIÈRE A BOUGÉ DEUX FOIS, et il faut le dire ici parce que ce commentaire a décrit
     *  les deux états précédents. Au départ, la charte se partageait en deux langues : la teinte
     *  du joueur pour CE QU'IL RESTE À FAIRE — cases marquées, quadrillage, compte par zone,
     *  boussole — et l'accent pour CE QU'ON VIENT DE FAIRE. Puis la teinte a débordé sur
     *  l'interface entière, jusqu'aux filtres du catalogue et au menu du bas, au motif qu'un
     *  joueur qui a choisi le rose n'attend pas un palier citron au milieu de tout ça.
     *
     *  Ce débordement est annulé, et c'est l'arrivée des THÈMES qui l'a tranché : avec trois
     *  habillages au choix, un onglet rose posé à côté d'un accent violet ne se lit plus comme
     *  un réglage mais comme une erreur. L'interface est revenue à l'accent du thème (cf. les
     *  blocs [data-skin] de style.css). La teinte garde tout ce qui est CI-DESSOUS — la feuille,
     *  donc — plus le seul morceau d'interface qui la double : la pastille verrouillée de la
     *  palette et sa jauge, dont l'anneau porte la même couleur que les cases marquées à côté.
     *
     *  Ce qui reste à l'accent, et pourquoi : la vague et l'anneau d'une case posée, qui sont le
     *  retour du GESTE — ils accompagnent un doigt, ils ne récompensent rien — et le balayage de
     *  la fin de partie, qui n'appartient plus à la partie. Le palier d'un symbole, lui, est une
     *  récompense, et une récompense parle la langue du joueur. */
    zoneLine: a(mk, 0.3),
    zoneLineBare: a(mk, 0.5),
    /** The plate's own rule, matching the lattice it belongs to. */
    plateEdge: a(mk, 0.5),
    /** La boussole du bord d'écran. Un disque PLEIN à l'accent vif, là où les compteurs de
     *  zone se posent sur une plaque couleur papier : eux s'affichent au-dessus du dessin, où
     *  le papier tranche ; elle s'affiche sur une portion de feuille vide, où une plaque de
     *  papier ne se voit pas. La règle de la charte tient dans les deux cas — l'accent vif
     *  pour une SURFACE, le foncé pour ce qui s'écrit dessus. */
    guide: a(mk, 0.94),
    guideEdge: a(mkDeep, 0.5),
    guideShadow: a(shade, 0.22),
    /** Le chevron, dans un sombre qui ne suit NI l'encre NI le thème : la boussole appartient
     *  à l'interface, pas au dessin, et elle se pose sur un disque à la teinte du joueur — un
     *  chevron qui s'éclaircirait avec le thème disparaîtrait dessus. */
    guideSign: `rgb(${onGuide})`,
    /** A zone count. Le ton FONCÉ du repère et non le vif : le vif est illisible sur du crème. */
    zoneNum: a(mkDeep, 0.95),
    /** The paper's ruling, and the code printed in a cell still to fill. */
    ruling: a(rule, 0.13),
    code: a(rule, 0.42),
    /** Le repère des cases à faire — cf. src/mark.ts pour le choix, et drawMarkStatic dans
     *  render.ts pour ce que chacune de ces couleurs dessine. Toutes se
     *  composent de la TEINTE DU REPÈRE et non de l'accent : c'est un réglage du joueur.
     *
     *  Le lavis est bien plus fort qu'il n'en a l'air besoin (.34 auparavant), et il peut se le
     *  permettre : une case marquée est par définition VIDE, donc ce lavis n'a aucun dessin à
     *  repeindre — la retenue qu'exige le lavis de la récolte ne s'applique pas ici. Ce qu'il
     *  achète, c'est que la case se lise comme COLORÉE et non seulement comme claire, seule
     *  chose qui la distingue des cases vides de tous les autres symboles autour. Le chiffre
     *  imprimé dessous s'embrouille, et ça ne coûte rien : les cases marquées sont celles d'un
     *  symbole VERROUILLÉ, donc le chiffre redit ce que le joueur vient de choisir. */
    wash0: a(mk, 0.58),
    wash1: a(mkDeep, 0.3),
    /** Le même lavis, en retrait, pour « une croix par case » : là la croix porte le repère, et
     *  un lavis à pleine force lutterait avec elle au lieu de l'asseoir. */
    washPale0: a(mk, 0.22),
    washPale1: a(mkDeep, 0.12),
    hatch: a(mkDeep, 0.34),
    /** Les deux tons du bord : la bande éclairée, puis le trait de plume en son milieu.
     *
     *  Un seul ton ne peut pas faire le travail, parce que le repère doit tenir sur deux fonds
     *  opposés. Au début il se pose sur du crème nu et un lavis pâle suffit largement. En fin de
     *  partie il se pose dans un champ de graphite — et là l'ancien marquage disparaissait presque :
     *  sa lueur est un débord pâle conçu pour tomber sur le PAPIER, et dans l'encre dense elle tombe
     *  sur du noir et s'efface, laissant la case se lire comme du papier vaguement teinté. Pire, sa
     *  vraie concurrence là-dedans, ce sont les cases vides des AUTRES symboles, qui sont des trous
     *  clairs dans l'encre sombre et crient plus fort qu'un écart de teinte ne le pourra jamais.
     *  Bande claire plus trait sombre donne à la case un bord qui se lit contre l'encre et un qui
     *  se lit contre le papier, donc elle ne dépend plus de ce qui l'entoure. */
    rim: a(mk, 0.95),
    edge: a(mkDeep, 0.92),
    glowOut: a(mkLight, 0.11),
    /** Le halo autour du contour, et le point posé sur chaque case. Opaques : leur transparence
     *  était celle d'un battement, donc posée en globalAlpha et non ici. Le battement est parti,
     *  le globalAlpha reste — désormais une constante, lisible à côté de la largeur qu'elle
     *  accompagne dans drawMarkStatic plutôt qu'enfouie dans une couleur. */
    markGlow: `rgb(${mk})`,
    markDot: `rgb(${mk})`,
    /** The wave's leading edge, tinted; the second form is for the alphabets that carry their
     *  own colours, where a flat accent would repaint the pigments. */
    wave: `rgb(${acc})`,
    waveSelf: a(light, 0.62),
    /** « La récolte » — the light that runs once over the cells of a symbol just finished, and the
     *  wash it leaves behind it. Brighter and tighter than the highlight's own sheen: that one
     *  loops as an ambient marking of work still to do, this one passes a single time and IS the
     *  reward. The wash started out faint, to avoid repainting the drawing it congratulates, and
     *  that turned out to be the wrong worry: at whole-sheet zoom, where the reward is watched, a
     *  cell is under 3px and a faint wash simply averages into the paper. It is the shadow below
     *  that keeps the drawing readable, and the wash's job is to light the harvest out of it. */
    harvestOff: a(mkLight, 0),
    harvestOn: a(mkSoft, 0.62),
    harvestTail: a(mk, 0.42),
    /** And the shadow the rest of the sheet falls into while that light is out: the ground's own
     *  colour, as if the sheet were lit from off the page. It carries the effect once the camera
     *  has pulled back — see the shadow comment in drawHarvest for why a wash cannot. */
    harvestShade: a(shade, 0.32),
    /** The colour ink has at the instant it lands, before it cools to graphite: one fill
     *  blooming in, and the replay's trail across a whole sheet. */
    fresh: `rgb(${fresh})`,
    /** The ring a fill throws off. */
    ring: a(acc, 1),
    /** Le balayage de la feuille terminée, et le filet du passe-partout qui l'encadre — cf.
     *  paintMat() dans render.ts. À LA TEINTE DU JOUEUR comme la récolte : la fin d'une feuille
     *  est la plus grande des récompenses, et une récompense parle sa langue.
     *
     *  Le filet prend le ton FONCÉ et non le vif, pour la raison que zoneNum donne déjà : il est
     *  tracé sur du crème, où le vif est illisible. */
    sweepOff: a(mk, 0),
    sweepOn: a(mkSoft, 0.85),
    matEdge: a(mkDeep, 0.8),
    /** A cell of the wrong symbol. */
    mistake: `rgb(${red})`,
    mistakeFill: a(red, 0.22),
  };
}

let cached: ReturnType<typeof build> | null = null;

/** The charter's colours, composed for the canvas. Cheap to call — it is one property read
 *  after the first frame. */
export function pal(): ReturnType<typeof build> {
  return (cached ??= build());
}

/** À appeler quand une des sources change — pour l'instant la seule est la teinte du repère.
 *  Jeter le cache suffit : la prochaine image le recompose. */
export function resetPal(): void {
  cached = null;
}
